/**
 * Scene 1 harness — owns state machine, idle timers, and dispatch.
 * Spec: BLUEPRINT.md § Scene 1, design/act-1-dystopia/DESIGN.md § Scene 1.
 */
import { chat } from "../lib/chat";
import { runAwaitLaunch } from "../lib/awaitLaunch";
import { consumeLine } from "../lib/debounce";
import { consumeFlightHint } from "../lib/flightHint";
import type { SceneDeps, SceneModule, SceneRunResult } from "../sceneModule";
import { SCENE1_LINES } from "./lines";
import { awaitAckAdvances, isCleanRefusal } from "./regex";

type State =
  | "COLD_OPEN"
  | "AWAIT_ACK"
  | "AWAIT_ACCEPT"
  | "AWAIT_LAUNCH"
  | "FLIGHT"
  | "PAUSED"
  | "CLOSE"
  | "END";

const SCENE1_PRE_PAYSTUB_DEBT = 318_442;

const COLD_OPEN_BEAT_MS = 800;
const COLD_OPEN_INTER_LINE_MS = 400;
const GATE1_COMMIT_DELAY_MS = 600;
const DECLINE_REPRISE_MS = 2000;
const IDLE_30S_MS = 30_000;
const IDLE_90S_MS = 90_000;
const MAX_HISTORY_TURNS = 12;

export function startScene1(deps: SceneDeps): SceneModule {
  const { chat: chatPanel, scene, cubicle } = deps;

  let state: State = "COLD_OPEN";
  let inFlight = false;
  const debounceState = { lastFiredAt: new Map<string, number>() };
  let idle30: ReturnType<typeof setTimeout> | null = null;
  let idle90: ReturnType<typeof setTimeout> | null = null;
  let purchaseActive = false;
  let purchaseSilenceTimer: ReturnType<typeof setTimeout> | null = null;
  let vitalixDeducted = false;
  let endResolve: ((r: SceneRunResult) => void) | null = null;

  function clearIdleTimers(): void {
    if (idle30) { clearTimeout(idle30); idle30 = null; }
    if (idle90) { clearTimeout(idle90); idle90 = null; }
  }

  // Only used for AWAIT_ACK; AWAIT_LAUNCH is owned by runAwaitLaunch.
  function scheduleIdleTimers(): void {
    clearIdleTimers();
    idle30 = setTimeout(() => {
      if (state === "AWAIT_ACK" && !inFlight) void chatPanel.appendQT(SCENE1_LINES.idleAwaitAck30s);
    }, IDLE_30S_MS);
    idle90 = setTimeout(() => {
      if (state === "AWAIT_ACK" && !inFlight) void chatPanel.appendQT(SCENE1_LINES.idleAwaitAck90s);
    }, IDLE_90S_MS);
  }

  async function callHaiku(
    pilotUtterance: string,
    historyOverride?: { role: "user" | "assistant"; content: string }[],
  ): Promise<{ text: string; actions: { name: string; input: Record<string, unknown> }[] }> {
    inFlight = true;
    clearIdleTimers();
    try {
      const response = await chat({
        character: "qt",
        trigger: "pilot_submit",
        pilot_utterance: pilotUtterance,
        game_state: {},
        history: historyOverride ?? [...deps.history],
      });
      deps.history.push({ role: "user", content: pilotUtterance });
      if (response.text) deps.history.push({ role: "assistant", content: response.text });
      while (deps.history.length > MAX_HISTORY_TURNS) deps.history.shift();
      return { text: response.text, actions: response.actions };
    } finally {
      inFlight = false;
      if (state === "AWAIT_ACK") scheduleIdleTimers();
    }
  }

  // ─── Auto-purchase helpers ───────────────────────────────────────
  function fireDeduction(): void {
    vitalixDeducted = true;
    chatPanel.appendSystem({
      header: SCENE1_LINES.autoPurchaseScene1.deductionBlock.header,
      body: [...SCENE1_LINES.autoPurchaseScene1.deductionBlock.body],
    });
  }

  async function resolvePurchaseNonRefusal(playerText: string): Promise<void> {
    purchaseActive = false;
    if (purchaseSilenceTimer) { clearTimeout(purchaseSilenceTimer); purchaseSilenceTimer = null; }
    const purchaseHistory = [
      ...deps.history,
      { role: "assistant" as const, content: SCENE1_LINES.autoPurchaseScene1.proposal },
    ];
    const { text: coachingText } = await callHaiku(playerText, purchaseHistory);
    if (state === "CLOSE" || state === "END") return;
    const coaching = coachingText ||
      SCENE1_LINES.autoPurchaseScene1.nearMissCoaching[
        Math.floor(Math.random() * SCENE1_LINES.autoPurchaseScene1.nearMissCoaching.length)
      ];
    void chatPanel.appendQT(coaching);
    fireDeduction();
  }

  async function firePurchaseProposal(): Promise<void> {
    if (!(["FLIGHT", "PAUSED"] as State[]).includes(state)) return;
    purchaseActive = true;
    await chatPanel.appendQT(SCENE1_LINES.autoPurchaseScene1.bodyDecayLead);
    await new Promise<void>((r) => setTimeout(r, 1500));
    if (state === "CLOSE" || state === "END") { purchaseActive = false; return; }
    await chatPanel.appendQT(SCENE1_LINES.autoPurchaseScene1.proposal);
    purchaseSilenceTimer = setTimeout(() => {
      if (!purchaseActive) return;
      purchaseActive = false;
      purchaseSilenceTimer = null;
      fireDeduction();
    }, 6_000);
  }

  // ─── Pilot submit handler ────────────────────────────────────────
  chatPanel.onPilotSubmit((text) => { void handlePilotSubmit(text); });

  async function handlePilotSubmit(text: string): Promise<void> {
    if (state === "CLOSE" || state === "END" || state === "COLD_OPEN" || state === "AWAIT_LAUNCH") return;
    chatPanel.appendPilot(text);

    if (state === "PAUSED") {
      resumeFlight();
      // state is now FLIGHT — fall through
    }

    if (state === "AWAIT_ACK") {
      if (!awaitAckAdvances(text)) return;
      const { text: replyText } = await callHaiku(text);
      if (replyText) await chatPanel.appendQT(replyText);
      setTimeout(() => {
        if (state !== "AWAIT_ACK") return;
        clearIdleTimers();
        state = "AWAIT_ACCEPT";
        renderAssignment();
        void chatPanel.appendQT(SCENE1_LINES.gate2Framing);
      }, GATE1_COMMIT_DELAY_MS);
      return;
    }

    if (state === "AWAIT_ACCEPT") {
      const { text: replyText } = await callHaiku(text);
      if (replyText) await chatPanel.appendQT(replyText);
      return;
    }

    if ((state === "FLIGHT" || state === "PAUSED") && purchaseActive) {
      if (isCleanRefusal(text)) {
        purchaseActive = false;
        if (purchaseSilenceTimer) { clearTimeout(purchaseSilenceTimer); purchaseSilenceTimer = null; }
        await chatPanel.appendQT(SCENE1_LINES.autoPurchaseScene1.cleanRefusal);
        return;
      }
      void resolvePurchaseNonRefusal(text);
      return;
    }

    if (state === "FLIGHT") {
      const { text: replyText } = await callHaiku(text);
      if (replyText) await chatPanel.appendQT(replyText);
    }
  }

  // ─── Pause / resume during FLIGHT ────────────────────────────────
  function pauseFlight(): void {
    if (state !== "FLIGHT") return;
    scene.pauseScene1();
    cubicle.setLiveMonitor("chat-live");
    chatPanel.focusInput();
    state = "PAUSED";
  }

  function resumeFlight(): void {
    if (state !== "PAUSED") return;
    chatPanel.blurInput();
    scene.resumeScene1();
    cubicle.setLiveMonitor("game-live");
    state = "FLIGHT";
  }

  cubicle.onMonitorClick((which) => {
    if (which === "chat") {
      if (state === "FLIGHT") pauseFlight();
      else if (state === "END") { cubicle.setLiveMonitor("chat-live"); chatPanel.focusInput(); }
      else chatPanel.focusInput();
    } else {
      if (state === "PAUSED") resumeFlight();
      else if (state === "FLIGHT") chatPanel.blurInput();
      else if (state === "END") { cubicle.setLiveMonitor("chat-live"); chatPanel.focusInput(); }
    }
  });

  // ─── Assignment block render ─────────────────────────────────────
  let assignmentHandle: { collapse(): void } | null = null;
  function renderAssignment(): void {
    assignmentHandle = chatPanel.appendSystem({
      header: SCENE1_LINES.assignment.header,
      body: [...SCENE1_LINES.assignment.body],
      buttons: [
        {
          label: SCENE1_LINES.assignment.buttons.accept,
          onClick: async () => {
            if (state !== "AWAIT_ACCEPT") return;
            assignmentHandle?.collapse();
            assignmentHandle = null;
            await chatPanel.appendQT(SCENE1_LINES.gate2Accept);
            state = "AWAIT_LAUNCH";
            await runAwaitLaunch({
              chat: chatPanel, scene, cubicle, history: deps.history,
              lines: {
                idleAwaitLaunch30s: SCENE1_LINES.idleAwaitLaunch30s,
                idleAwaitLaunch90s: SCENE1_LINES.idleAwaitLaunch90s,
                launchAckRegex: SCENE1_LINES.launchAckRegex,
              },
              onLaunch: () => {
                state = "FLIGHT";
                if (consumeFlightHint()) void chatPanel.appendQT(SCENE1_LINES.flightEntryHint);
                setTimeout(() => { void firePurchaseProposal(); }, 20_000);
              },
            });
            chatPanel.onPilotSubmit((text) => { void handlePilotSubmit(text); });
          },
        },
        {
          label: SCENE1_LINES.assignment.buttons.decline,
          onClick: () => {
            if (state !== "AWAIT_ACCEPT") return;
            void chatPanel.appendQT(SCENE1_LINES.gate2Decline);
            setTimeout(() => { if (state === "AWAIT_ACCEPT") renderAssignment(); }, DECLINE_REPRISE_MS);
          },
        },
      ],
    });
  }

  // ─── Hull-strike listener ────────────────────────────────────────
  // Scene 1 has no DAMAGE_PAUSE; auto-pause has already happened, so resume immediately.
  function hullStrikeHandler(): void {
    if (state !== "FLIGHT" && state !== "PAUSED") return;
    const text = consumeLine(SCENE1_LINES.hullStrike, debounceState);
    if (text) void chatPanel.appendQT(text);
    scene.resumeScene1();
  }
  scene.events.on("hull-strike", hullStrikeHandler);

  // ─── Deposit listener — atomic CLOSE ─────────────────────────────
  function depositHandler(): void {
    if (purchaseActive) {
      purchaseActive = false;
      if (purchaseSilenceTimer) { clearTimeout(purchaseSilenceTimer); purchaseSilenceTimer = null; }
    }
    if (state !== "FLIGHT") return;
    clearIdleTimers();
    scene.pauseScene1();
    cubicle.setLiveMonitor("chat-live");
    chatPanel.focusInput();
    const paystubBody = [
      ...SCENE1_LINES.paystubBlock.bodyPre,
      ...(vitalixDeducted ? [SCENE1_LINES.paystubBlock.vitalixLine] : []),
      ...SCENE1_LINES.paystubBlock.bodyPost,
    ];
    chatPanel.appendSystem({ header: SCENE1_LINES.paystubBlock.header, body: paystubBody });
    void chatPanel.appendQT(SCENE1_LINES.closeLine);
    cubicle.updateDebtCounter(
      (SCENE1_PRE_PAYSTUB_DEBT + (vitalixDeducted ? 16 : 4)).toLocaleString("en-US"),
      true,
    );
    state = "END";
    teardown();
    endResolve?.({});
  }
  scene.events.on("deposit", depositHandler);

  // ─── Teardown — remove listeners when scene ends ─────────────────
  function teardown(): void {
    scene.events.off("hull-strike", hullStrikeHandler);
    scene.events.off("deposit", depositHandler);
    cubicle.onMonitorClick(() => {});
    chatPanel.onPilotSubmit(() => {});
  }

  return {
    async run(): Promise<SceneRunResult> {
      state = "COLD_OPEN";
      await new Promise((r) => setTimeout(r, COLD_OPEN_BEAT_MS));
      for (let i = 0; i < SCENE1_LINES.coldOpen.length; i++) {
        await chatPanel.appendQT(SCENE1_LINES.coldOpen[i]);
        if (i < SCENE1_LINES.coldOpen.length - 1) {
          await new Promise((r) => setTimeout(r, COLD_OPEN_INTER_LINE_MS));
        }
      }
      state = "AWAIT_ACK";
      scheduleIdleTimers();
      return new Promise<SceneRunResult>((resolve) => { endResolve = resolve; });
    },
  };
}
